import React, { useEffect, useState } from "react";
import { MagnifyingGlassIcon, ArrowPathIcon, XMarkIcon, BoltIcon, TagIcon, DocumentTextIcon, ClockIcon, UserIcon } from "@heroicons/react/24/outline";
import { getUserAnalytics } from "../api/monitoringAPI";
import { getAdminUsers, getAdminUserById } from "../api/adminUsers";
import { UserAvatar, TypeBadge } from "./users/UserDetailsPanel";
import { EventRow } from "./EventsList";
import { Card, Kpi, KpiSkeleton, ErrorState, Empty, RankList } from "./monitoring/ui";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const UserAnalytics = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState(null);

  // Имя/телефон/email → список; число → сразу ID
  useEffect(() => {
    const q = query.trim();
    if (!q || /^#?\d{1,7}$/.test(q)) {
      setResults([]);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        setResults((await getAdminUsers({ q, limit: 8 })).items || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const open = async (id) => {
    setResults([]);
    setQuery("");
    setState("loading");
    setError(null);
    setAnalytics(null);
    setUser(null);
    const [profile, data] = await Promise.allSettled([getAdminUserById(id), getUserAnalytics(String(id))]);
    setUser(profile.status === "fulfilled" ? profile.value : { id: Number(id) });
    if (data.status === "fulfilled") {
      setAnalytics(data.value);
      setState("ready");
    } else {
      setError(data.reason?.message);
      setState("error");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const q = query.trim().replace(/^#/, "");
    if (/^\d{1,7}$/.test(q)) open(q);
    else if (results.length === 1) open(results[0].id);
  };

  const types = [...(analytics?.event_type_breakdown || [])]
    .map((t) => ({ label: t.event_type || t.type || "—", value: t.count || t.total || 0 }))
    .sort((a, b) => b.value - a.value);
  const pages = (analytics?.most_visited_pages || []).slice(0, 10).map((p) => ({ label: p.page || p.path || p.url || "—", value: p.count || p.visits || 0 }));
  const events = analytics?.events || [];

  return (
    <div className="space-y-4">
      <Card bodyClassName="p-4">
        {user ? (
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="h-11 w-11 text-sm" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-text-primary">{user.name || "Без имени"}</span>
                {user.user_type && <TypeBadge type={user.user_type} />}
              </div>
              <p className="truncate text-xs text-text-secondary">
                <span className="font-mono">#{user.id}</span>
                {user.phone_number && ` · ${user.phone_number}`}
                {user.email && ` · ${user.email}`}
              </p>
            </div>
            <button type="button" onClick={() => open(user.id)} title="Обновить" aria-label="Обновить" className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
              <ArrowPathIcon className={`h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => {
                setUser(null);
                setAnalytics(null);
                setState("idle");
              }}
              title="Другой пользователь"
              aria-label="Другой пользователь"
              className="rounded-lg p-2 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-text-muted" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ID, имя, телефон или email пользователя — Enter для ID"
              aria-label="Найти пользователя"
              className="w-full rounded-lg border border-border-primary bg-bg-primary py-2.5 pl-9 pr-10 text-sm text-text-primary placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30"
            />
            {searching && <ArrowPathIcon className="absolute right-3 top-3 h-4 w-4 animate-spin text-text-muted" />}
            {results.length > 0 && (
              <ul className="mt-2 divide-y divide-border-primary overflow-hidden rounded-lg border border-border-primary">
                {results.map((u) => (
                  <li key={u.id}>
                    <button type="button" onClick={() => open(u.id)} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-bg-tertiary">
                      <UserAvatar user={u} size="h-8 w-8 text-xs" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-text-primary">
                          {u.name || "Без имени"} <span className="font-mono text-xs text-text-muted">#{u.id}</span>
                        </span>
                        <span className="block truncate text-xs text-text-secondary">{u.phone_number || u.email || "—"}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </form>
        )}
      </Card>

      {state === "idle" && (
        <div className="rounded-block border border-dashed border-border-primary bg-bg-card px-6 py-14 text-center text-text-muted">
          <UserIcon className="mx-auto mb-2 h-10 w-10" />
          <p className="text-sm">Найдите пользователя, чтобы увидеть его действия в приложении</p>
        </div>
      )}

      {state === "loading" && (
        <>
          <KpiSkeleton />
          <div className="h-64 animate-pulse rounded-block border border-border-primary bg-bg-card" />
        </>
      )}

      {state === "error" && <ErrorState text="Не удалось загрузить аналитику пользователя" detail={error} onRetry={() => open(user.id)} />}

      {state === "ready" && analytics && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Событий" value={analytics.total_events ?? events.length} icon={BoltIcon} />
            <Kpi label="Типов событий" value={types.length} icon={TagIcon} tone="bg-amber-500/15 text-amber-500" />
            <Kpi label="Первая активность" value={analytics.first_activity ? new Date(analytics.first_activity).toLocaleDateString("ru-RU") : "—"} hint={fmtDate(analytics.first_activity)} icon={ClockIcon} tone="bg-sky-500/15 text-sky-500" />
            <Kpi label="Последняя активность" value={analytics.last_activity ? new Date(analytics.last_activity).toLocaleDateString("ru-RU") : "—"} hint={fmtDate(analytics.last_activity)} icon={ClockIcon} tone="bg-emerald-500/15 text-emerald-500" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Что делал пользователь" subtitle="Типы событий">
              <div className="max-h-96 overflow-y-auto">
                <RankList items={types} emptyText="Событий нет" />
              </div>
            </Card>
            <Card title="Экраны и страницы" subtitle={`${analytics.most_visited_pages?.length || 0} уникальных`}>
              <RankList items={pages} emptyText="Нет данных о посещениях" color="bg-sky-500" />
            </Card>
          </div>

          <Card title="Лента событий" subtitle={events.length ? `${events.length} последних — нажмите, чтобы раскрыть` : undefined} actions={<DocumentTextIcon className="h-4 w-4 text-text-muted" />}>
            {events.length ? (
              <ul className="max-h-[32rem] divide-y divide-border-primary overflow-y-auto">
                {events.map((e, i) => (
                  <EventRow key={e.id || `${e.timestamp}-${i}`} event={e} />
                ))}
              </ul>
            ) : (
              <Empty>Событий нет</Empty>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default UserAnalytics;
